import React, { useState, useRef, useCallback, useEffect } from "react";
import axios from "../utils/axiosInstance";
import { useToast } from "./ConfirmDialog";
import { io } from "socket.io-client";

const API = "";

const SOCKET_URL = process.env.REACT_APP_API_URL || (typeof window !== "undefined" ? window.location.origin : "http://localhost:5000");

const Styles = () => (
  <style>{`
@import url('https://fonts.googleapis.com/css2?family=Nunito+Sans:wght@300;400;500;600;700;800;900&family=Noto+Serif:wght@400;700&display=swap');

/* ── Page Background ── */
.gdoc-body {
  min-height: 100vh;
  background: #e8ecf1;
  padding: 30px 16px 30px;
  display: flex;
  flex-direction: column;
  align-items: center;
}

/* ── A4 Paper Container ── */
.gdoc-paper {
  width: 100%;
  max-width: 900px;
  background: #ffffff;
  border-radius: 6px;
  box-shadow: 0 4px 24px rgba(0,0,0,0.10), 0 1px 4px rgba(0,0,0,0.06);
  padding: 50px 55px 40px;
  margin: 0 auto;
  position: relative;
  font-family: 'Nunito Sans', system-ui, sans-serif;
  color: #1e293b;
  transition: all 0.2s ease;
}

/* ── Document Header ── */
.gdoc-header {
  text-align: center;
  padding-bottom: 28px;
  border-bottom: 3px solid #1a3a5c;
  margin-bottom: 28px;
  position: relative;
}
.gdoc-header::after {
  content: '';
  position: absolute;
  bottom: -6px;
  left: 50%;
  transform: translateX(-50%);
  width: 80px;
  height: 3px;
  background: #d4a843;
  border-radius: 2px;
}
.gdoc-header-republic {
  font-family: 'Noto Serif', serif;
  font-size: 18px;
  font-weight: 700;
  color: #1a3a5c;
  letter-spacing: 2.5px;
  text-transform: uppercase;
  margin: 0 0 4px;
}
.gdoc-header-title {
  font-family: 'Noto Serif', serif;
  font-size: 22px;
  font-weight: 700;
  color: #b8860b;
  text-transform: uppercase;
  letter-spacing: 1.5px;
  margin: 0 0 12px;
}
.gdoc-header-docnum {
  display: inline-block;
  font-size: 13px;
  font-weight: 600;
  color: #ffffff;
  background: #1a3a5c;
  padding: 6px 18px;
  border-radius: 20px;
  letter-spacing: 0.5px;
}
.gdoc-header-motto {
  font-size: 11px;
  color: #64748b;
  margin-top: 6px;
  font-style: italic;
}

/* ── Header Row (date + village) ── */
.gdoc-top-row {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 20px;
  margin-bottom: 24px;
  flex-wrap: wrap;
}
.gdoc-village-info {
  flex: 1;
  min-width: 200px;
}
.gdoc-village-info h4 {
  font-size: 12px;
  font-weight: 700;
  color: #1a3a5c;
  text-transform: uppercase;
  letter-spacing: 1px;
  margin: 0 0 8px;
  padding-bottom: 4px;
  border-bottom: 1px solid #e2e8f0;
}
.gdoc-village-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 3px 16px;
}
.gdoc-village-item {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  color: #475569;
}
.gdoc-village-item strong {
  font-weight: 600;
  color: #1e293b;
  min-width: 58px;
}
.gdoc-date-box {
  text-align: right;
  min-width: 130px;
  padding: 10px 14px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  border-left: 3px solid #1a3a5c;
}
.gdoc-date-box .gdoc-date-label {
  font-size: 10px;
  font-weight: 700;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}
.gdoc-date-box .gdoc-date-value {
  font-size: 14px;
  font-weight: 600;
  color: #1e293b;
  margin-top: 2px;
}

/* ── Document Sections ── */
.gdoc-section {
  margin-bottom: 24px;
  position: relative;
}
.gdoc-section-title {
  font-size: 13px;
  font-weight: 700;
  color: #1a3a5c;
  text-transform: uppercase;
  letter-spacing: 1px;
  padding: 8px 14px;
  background: #f1f5f9;
  border-left: 4px solid #1a3a5c;
  margin: 0 0 14px;
  display: flex;
  align-items: center;
  gap: 8px;
}
.gdoc-section-title i { font-size: 14px; color: #d4a843; }
.gdoc-section-body {
  padding: 0 4px;
}

/* ── Form Fields ── */
.gdoc-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 14px 20px;
  margin-bottom: 4px;
}
.gdoc-row-full {
  grid-template-columns: 1fr;
}
.gdoc-field {
  margin-bottom: 2px;
}
.gdoc-field label {
  display: block;
  font-size: 11px;
  font-weight: 600;
  color: #475569;
  margin-bottom: 4px;
  letter-spacing: 0.3px;
}
.gdoc-field label .required { color: #dc2626; }
.gdoc-field input,
.gdoc-field select,
.gdoc-field textarea {
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #d1d9e6;
  border-radius: 5px;
  font-size: 14px;
  color: #1e293b;
  background: #f8fafc;
  transition: all 0.2s ease;
  font-family: inherit;
}
.gdoc-field input:focus,
.gdoc-field select:focus,
.gdoc-field textarea:focus {
  outline: none;
  border-color: #1a3a5c;
  box-shadow: 0 0 0 3px rgba(26,58,92,0.10);
  background: #ffffff;
}
.gdoc-field input:disabled,
.gdoc-field select:disabled,
.gdoc-field textarea:disabled {
  background: #f1f5f9;
  color: #64748b;
  cursor: not-allowed;
}
.gdoc-field textarea {
  min-height: 100px;
  resize: vertical;
  line-height: 1.6;
}
.gdoc-field .gdoc-char-count {
  font-size: 11px;
  color: #94a3b8;
  text-align: right;
  margin-top: 3px;
}
.gdoc-field .gdoc-sub {
  font-size: 11px;
  color: #94a3b8;
  margin-top: 3px;
}
.gdoc-description-area {
  min-height: 250px !important;
}
.gdoc-bordered-box {
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  padding: 16px;
  background: #fafbfc;
  margin-bottom: 12px;
}

/* ── Witness Cards ── */
.gdoc-witness-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-bottom: 14px;
}
.gdoc-witness-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 14px;
  background: #f0fdf4;
  border: 1px solid #bbf7d0;
  border-radius: 6px;
  font-size: 13px;
  color: #065f46;
}
.gdoc-witness-item i { color: #16a34a; }
.gdoc-witness-item .gdoc-witness-name { font-weight: 600; flex: 1; }
.gdoc-witness-item .gdoc-witness-remove {
  background: none;
  border: none;
  color: #dc2626;
  cursor: pointer;
  font-size: 16px;
  padding: 4px;
  line-height: 1;
}
.gdoc-witness-add-card {
  background: #f8fafc;
  border: 1px dashed #cbd5e1;
  border-radius: 6px;
  padding: 16px;
}
.gdoc-witness-add-card h5 {
  font-size: 13px;
  font-weight: 600;
  color: #1e293b;
  margin: 0 0 10px;
  display: flex;
  align-items: center;
  gap: 6px;
}

/* ── Signature Section ── */
.gdoc-signatures {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 14px;
  margin-top: 8px;
}
.gdoc-signature-slot {
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  padding: 14px;
  text-align: center;
  background: #fafbfc;
}
.gdoc-signature-slot .gdoc-sig-label {
  font-size: 10px;
  font-weight: 700;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 4px;
}
.gdoc-signature-slot .gdoc-sig-name {
  font-size: 13px;
  font-weight: 600;
  color: #1e293b;
  margin-bottom: 8px;
  min-height: 20px;
}
.gdoc-signature-slot .gdoc-sig-placeholder {
  font-size: 11px;
  color: #94a3b8;
  font-style: italic;
  min-height: 50px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px dashed #d1d9e6;
  border-radius: 4px;
  padding: 8px;
  background: #fff;
}
.gdoc-signature-slot .gdoc-sig-image {
  max-height: 50px;
  display: block;
  margin: 0 auto;
  border-radius: 3px;
  border: 1px solid #e2e8f0;
  padding: 4px;
  background: #fff;
}
.gdoc-sig-status {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 10px;
  font-weight: 600;
  padding: 2px 8px;
  border-radius: 10px;
  margin-top: 6px;
}
.gdoc-sig-status.signed { background: #dcfce7; color: #16a34a; }
.gdoc-sig-status.pending { background: #fef3c7; color: #b45309; }

/* ── Signature Pad ── */
.gdoc-sig-pad-area {
  border: 2px dashed #cbd5e1;
  border-radius: 8px;
  padding: 14px;
  text-align: center;
  background: #f8fafc;
  margin-top: 6px;
}
.gdoc-sig-pad-area canvas {
  display: block;
  margin: 0 auto;
  width: 100%;
  max-width: 420px;
  height: auto;
  cursor: crosshair;
  touch-action: none;
  border-radius: 5px;
  background: #ffffff;
  border: 1px solid #e2e8f0;
}
.gdoc-sig-actions {
  display: flex;
  gap: 8px;
  justify-content: center;
  margin-top: 8px;
  flex-wrap: wrap;
}
.gdoc-sig-preview { max-height: 44px; display: block; margin: 6px auto; background: #fff; border-radius: 3px; padding: 3px; border: 1px solid #e2e8f0; }

/* ── Buttons ── */
.gdoc-actions {
  display: flex;
  gap: 10px;
  margin-top: 28px;
  padding-top: 24px;
  border-top: 2px solid #e2e8f0;
  flex-wrap: wrap;
  position: sticky;
  bottom: 0;
  background: #ffffff;
  z-index: 10;
}
.gdoc-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 10px 20px;
  border: none;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  font-family: inherit;
  text-decoration: none;
  min-height: 42px;
}
.gdoc-btn:disabled { opacity: 0.5; cursor: not-allowed; }
.gdoc-btn-primary { background: #1a3a5c; color: #fff; }
.gdoc-btn-primary:hover:not(:disabled) { background: #0f2a45; transform: translateY(-1px); box-shadow: 0 4px 12px rgba(26,58,92,0.3); }
.gdoc-btn-success { background: #15803d; color: #fff; }
.gdoc-btn-success:hover:not(:disabled) { background: #166534; transform: translateY(-1px); box-shadow: 0 4px 12px rgba(21,128,61,0.3); }
.gdoc-btn-warning { background: #d97706; color: #fff; }
.gdoc-btn-warning:hover:not(:disabled) { background: #b45309; }
.gdoc-btn-danger { background: #dc2626; color: #fff; }
.gdoc-btn-danger:hover:not(:disabled) { background: #b91c1c; }
.gdoc-btn-outline { background: transparent; color: #475569; border: 1px solid #d1d9e6; }
.gdoc-btn-outline:hover:not(:disabled) { background: #f1f5f9; border-color: #94a3b8; }
.gdoc-btn-sm { padding: 7px 14px; font-size: 12px; min-height: 34px; }
.gdoc-btn-lg { padding: 12px 28px; font-size: 14px; min-height: 48px; }
.gdoc-btn-block { width: 100%; justify-content: center; }
.gdoc-btn-gold { background: #d4a843; color: #1a3a5c; }
.gdoc-btn-gold:hover:not(:disabled) { background: #c49a38; }
.gdoc-btn-inline { display: inline-flex; width: auto; }

/* ── Step Indicator ── */
.gdoc-steps {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 24px;
  padding: 10px 14px;
  background: #f8fafc;
  border-radius: 8px;
  border: 1px solid #e2e8f0;
  flex-wrap: wrap;
}
.gdoc-step {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 11px;
  font-weight: 600;
  padding: 4px 10px;
  border-radius: 4px;
  color: #94a3b8;
  transition: all 0.2s ease;
}
.gdoc-step.completed { color: #15803d; background: #f0fdf4; }
.gdoc-step.active { color: #1a3a5c; background: #eff6ff; font-weight: 700; }
.gdoc-step.pending { color: #94a3b8; }
.gdoc-step-line { width: 20px; height: 2px; background: #e2e8f0; }
.gdoc-step-line.completed { background: #15803d; }

/* ── Preview Overlay ── */
.gdoc-preview-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.6);
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  overflow-y: auto;
}
.gdoc-preview-modal {
  background: #ffffff;
  width: 100%;
  max-width: 900px;
  max-height: 95vh;
  overflow-y: auto;
  border-radius: 8px;
  box-shadow: 0 24px 80px rgba(0,0,0,0.3);
  padding: 50px 55px 40px;
  position: relative;
  margin: auto;
}
.gdoc-preview-header {
  position: sticky;
  top: 0;
  background: #ffffff;
  z-index: 10;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0 0 16px;
  border-bottom: 2px solid #e2e8f0;
  margin: -50px -55px 28px;
  padding: 16px 55px;
}
.gdoc-preview-header h3 {
  font-size: 16px;
  font-weight: 700;
  color: #1a3a5c;
  margin: 0;
}
.gdoc-preview-close {
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
  color: #64748b;
  padding: 4px 8px;
  line-height: 1;
}
.gdoc-preview-close:hover { color: #1e293b; }

/* ── Send Section ── */
.gdoc-send-card {
  background: #f0fdf4;
  border: 1px solid #bbf7d0;
  border-radius: 8px;
  padding: 20px;
  text-align: center;
}
.gdoc-send-card h4 {
  font-size: 15px;
  font-weight: 700;
  color: #15803d;
  margin: 0 0 6px;
}
.gdoc-send-card p {
  font-size: 13px;
  color: #065f46;
  margin: 0 0 14px;
}
.gdoc-send-summary {
  display: flex;
  gap: 16px;
  justify-content: center;
  flex-wrap: wrap;
  margin-bottom: 14px;
}
.gdoc-send-summary-item {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  color: #065f46;
  background: #ffffff;
  padding: 8px 16px;
  border-radius: 6px;
  border: 1px solid #bbf7d0;
}

/* ── Success ── */
.gdoc-success {
  text-align: center;
  padding: 40px 20px;
}
.gdoc-success i {
  font-size: 56px;
  color: #16a34a;
  margin-bottom: 16px;
  display: block;
}
.gdoc-success h3 {
  font-size: 22px;
  font-weight: 700;
  color: #15803d;
  margin: 0 0 8px;
}
.gdoc-success p {
  font-size: 14px;
  color: #64748b;
  margin: 0 0 20px;
}

/* ── Loading/Progress ── */
.gdoc-loading {
  display: inline-flex;
  align-items: center;
  gap: 8px;
}
.gdoc-spinner {
  width: 16px;
  height: 16px;
  border: 2px solid rgba(255,255,255,0.3);
  border-top-color: #fff;
  border-radius: 50%;
  animation: gdocSpin 0.6s linear infinite;
}
@keyframes gdocSpin { to { transform: rotate(360deg); } }

/* ── Alert / Info ── */
.gdoc-alert {
  padding: 10px 14px;
  border-radius: 6px;
  font-size: 12px;
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 10px;
}
.gdoc-alert-info { background: #eff6ff; border: 1px solid #bfdbfe; color: #1e40af; }
.gdoc-alert-warning { background: #fef3c7; border: 1px solid #fde68a; color: #b45309; }
.gdoc-alert-success { background: #f0fdf4; border: 1px solid #bbf7d0; color: #15803d; }

/* ── Empty State ── */
.gdoc-empty {
  text-align: center;
  padding: 24px;
  color: #94a3b8;
  font-size: 13px;
}
.gdoc-empty i { font-size: 32px; margin-bottom: 8px; display: block; }

/* ── Print Styles ── */
@media print {
  @page { margin: 20mm; size: A4; }
  body { background: white !important; }
  .gdoc-body { background: white !important; padding: 0 !important; min-height: auto !important; }
  .gdoc-paper { box-shadow: none !important; padding: 0 !important; max-width: none !important; border-radius: 0 !important; }
  .gdoc-actions, .gdoc-btn, .gdoc-steps, .gdoc-preview-header, .gdoc-preview-overlay,
  .gdoc-step-line, .gdoc-witness-remove, .gdoc-sig-actions, .gdoc-sig-pad-area,
  .gdoc-alert { display: none !important; }
  .gdoc-signature-slot { border: 1px solid #ccc !important; break-inside: avoid; }
  .gdoc-paper { break-inside: avoid; }
  .gdoc-section { break-inside: avoid; }
}

/* ── Responsive ── */
@media (max-width: 768px) {
  .gdoc-body { padding: 16px 10px 16px; }
  .gdoc-paper { padding: 24px 20px; }
  .gdoc-row { grid-template-columns: 1fr; gap: 10px; }
  .gdoc-top-row { flex-direction: column; }
  .gdoc-date-box { text-align: left; width: 100%; }
  .gdoc-village-grid { grid-template-columns: 1fr; }
  .gdoc-signatures { grid-template-columns: 1fr 1fr; }
  .gdoc-preview-modal { padding: 24px 20px; }
  .gdoc-preview-header { margin: -24px -20px 20px; padding: 12px 20px; }
  .gdoc-actions { flex-direction: column; }
  .gdoc-btn { width: 100%; justify-content: center; }
}
@media (max-width: 480px) {
  .gdoc-paper { padding: 16px 14px; }
  .gdoc-header-republic { font-size: 15px; }
  .gdoc-header-title { font-size: 17px; }
  .gdoc-signatures { grid-template-columns: 1fr; }
}
  `}</style>
);

/* ── Digital Signature Pad ── */
function SignaturePad({ onSave, label }) {
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const lastPos = useRef(null);
  const [sigData, setSigData] = useState(null);

  const getPos = (e, canvas) => {
    const r = canvas.getBoundingClientRect();
    const src = e.touches ? e.touches[0] : e;
    return { x: (src.clientX - r.left) * (canvas.width / r.width), y: (src.clientY - r.top) * (canvas.height / r.height) };
  };

  const start = (e) => { e.preventDefault(); drawing.current = true; lastPos.current = getPos(e, canvasRef.current); };
  const move = (e) => {
    e.preventDefault();
    if (!drawing.current) return;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    const pos = getPos(e, canvas);
    ctx.beginPath();
    ctx.moveTo(lastPos.current.x, lastPos.current.y);
    ctx.lineTo(pos.x, pos.y);
    ctx.strokeStyle = "#1a3a5c";
    ctx.lineWidth = 2.5;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.stroke();
    lastPos.current = pos;
  };
  const stop = () => { drawing.current = false; };

  const isEmpty = () => {
    const canvas = canvasRef.current;
    if (!canvas) return true;
    const ctx = canvas.getContext("2d");
    const pixels = ctx.getImageData(0, 0, canvas.width, canvas.height).data;
    for (let i = 3; i < pixels.length; i += 4) {
      if (pixels[i] > 0) return false;
    }
    return true;
  };

  const handleSave = () => {
    if (isEmpty()) { alert("Nyamukono!"); return; }
    const data = canvasRef.current.toDataURL("image/png");
    setSigData(data);
    onSave(data);
  };
  const handleClear = () => {
    const canvas = canvasRef.current;
    if (canvas) canvas.getContext("2d").clearRect(0, 0, canvas.width, canvas.height);
    setSigData(null);
    onSave(null);
  };

  return (
    <div>
      <div className="gdoc-sig-pad-area">
        <canvas
          ref={canvasRef}
          width={420} height={100}
          onMouseDown={start} onMouseMove={move} onMouseUp={stop} onMouseLeave={stop}
          onTouchStart={start} onTouchMove={move} onTouchEnd={stop}
          style={{ display: "block", maxHeight: 100 }}
        />
        <div className="gdoc-sig-actions">
          <button onClick={handleClear} className="gdoc-btn gdoc-btn-sm gdoc-btn-danger" type="button">
            <i className="bi bi-eraser" /> Gukuraho
          </button>
          <button onClick={handleSave} className="gdoc-btn gdoc-btn-sm gdoc-btn-primary" type="button">
            <i className="bi bi-check-lg" /> Emeza Umukono
          </button>
        </div>
      </div>
      {sigData && (
        <div style={{ textAlign: "center", marginTop: 6 }}>
          <img src={sigData} alt="Umukono" className="gdoc-sig-preview" />
          <div style={{ fontSize: "11px", color: "#15803d", fontWeight: 600 }}>Umukono wemejwe</div>
        </div>
      )}
    </div>
  );
}

/* ── Generate Document Number ── */
function generateDocNumber() {
  const year = new Date().getFullYear();
  const rand = String(Math.floor(Math.random() * 999999) + 1).padStart(6, "0");
  return `IKR-${year}-${rand}`;
}

/* ── Format Date ── */
const formatDate = () => {
  return new Date().toLocaleDateString("rw-RW", { year: "numeric", month: "long", day: "numeric" });
};

/* ── Main Component ── */
export default function IbiregoForm() {
  const { toast } = useToast();
  const paperRef = useRef(null);
  const [step, setStep] = useState("form");
  const [complaintId, setComplaintId] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [docNumber] = useState(generateDocNumber);
  const [userVillage, setUserVillage] = useState({ intara: "", akarere: "", umurenge: "", akagari: "", umudugudu: "" });
  const [userName, setUserName] = useState("");

  const [form, setForm] = useState({
    complainant_name: "",
    national_id: "",
    phone: "",
    location: "",
    description: "",
    izina_isibo: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [complainantName, setComplainantName] = useState("");
  const [complainantSig, setComplainantSig] = useState(null);
  const [complainantSigConfirmed, setComplainantSigConfirmed] = useState(false);
  const [witnesses, setWitnesses] = useState([]);
  const [witnessName, setWitnessName] = useState("");
  const [witnessSig, setWitnessSig] = useState(null);
  const [witnessSaving, setWitnessSaving] = useState(false);
  const [sending, setSending] = useState(false);
  const [muduguduSigned, setMuduguduSigned] = useState(false);

  // Load user info from JWT
  useEffect(() => {
    try {
      const token = localStorage.getItem("token");
      if (token) {
        const payload = JSON.parse(atob(token.split(".")[1]));
        if (payload.fullname) setUserName(payload.fullname);
      }
    } catch {}
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === "national_id") {
      const cleaned = value.replace(/\D/g, "").slice(0, 16);
      setForm((prev) => ({ ...prev, [name]: cleaned }));
      return;
    }
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.complainant_name || !form.description || !form.izina_isibo) {
      toast({ type: "warning", title: "Ikosa", message: "Uzuza ibisabwa byose" });
      return;
    }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/api/isibo/ibirego`, form);
      toast({ type: "success", title: "Byakunze!", message: res.data.message });
      setComplaintId(res.data.id);
      setComplainantName(form.complainant_name);
      setStep("sign");
    } catch (err) {
      const msg = err.response?.data?.error || "Habaye ikosa";
      toast({ type: "danger", title: "Ikosa", message: msg });
    } finally {
      setSubmitting(false);
    }
  };

  const handleComplainantSign = (sigData) => {
    setComplainantSig(sigData);
  };

  const saveComplainantSignature = async () => {
    if (!complainantSig) {
      toast({ type: "warning", title: "Ikosa", message: "Nyamukono!" });
      return;
    }
    try {
      await axios.post(`${API}/api/isibo/ibirego/${complaintId}/signatures`, {
        signed_by: complainantName,
        signature_image: complainantSig,
      });
      toast({ type: "success", title: "Byakunze!", message: "Umukono wabitswe neza!" });
      setComplainantSigConfirmed(true);
      setStep("witnesses");
    } catch (err) {
      const msg = err.response?.data?.error || "Habaye ikosa";
      toast({ type: "danger", title: "Ikosa", message: msg });
    }
  };

  const saveWitness = async () => {
    if (!witnessName.trim()) {
      toast({ type: "warning", title: "Ikosa", message: "Andika izina ry'umuhamya" });
      return;
    }
    if (!witnessSig) {
      toast({ type: "warning", title: "Ikosa", message: "Umuhamya agilekwa umukono" });
      return;
    }
    setWitnessSaving(true);
    try {
      await axios.post(`${API}/api/isibo/ibirego/${complaintId}/abatangabuhamya`, {
        witness_name: witnessName.trim(),
        signature_image: witnessSig,
      });
      toast({ type: "success", title: "Byakunze!", message: "Umuhamya yanditswe neza!" });
      setWitnesses((prev) => [...prev, { witness_name: witnessName.trim(), signature_image: witnessSig }]);
      setWitnessName("");
      setWitnessSig(null);
    } catch (err) {
      const msg = err.response?.data?.error || "Habaye ikosa";
      toast({ type: "danger", title: "Ikosa", message: msg });
    } finally {
      setWitnessSaving(false);
    }
  };

  const removeWitness = async (index, witness) => {
    if (!witness.id) {
      setWitnesses((prev) => prev.filter((_, i) => i !== index));
      return;
    }
    try {
      await axios.delete(`${API}/api/isibo/ibirego/${complaintId}/abatangabuhamya/${witness.id}`);
      setWitnesses((prev) => prev.filter((_, i) => i !== index));
      toast({ type: "success", title: "Byakunze!", message: "Umuhamya yasibwe" });
    } catch {
      toast({ type: "danger", title: "Ikosa", message: "Ntibashoboye gusiba umuhamya" });
    }
  };

  const handleSendToUmudugudu = async () => {
    setSending(true);
    try {
      const res = await axios.patch(`${API}/api/isibo/ibirego/${complaintId}/send-to-umudugudu`);
      toast({ type: "success", title: "Byoherejwe!", message: res.data.message });
      setStep("ready");
    } catch (err) {
      const msg = err.response?.data?.error || "Habaye ikosa";
      toast({ type: "danger", title: "Ikosa", message: msg });
    } finally {
      setSending(false);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  const handlePreview = () => {
    setShowPreview(true);
  };

  const canAddWitness = witnesses.length < 5;

  /* ── Document Preview ── */
  const renderDocumentContent = (isPreview = false) => (
    <>
      {/* Document Header */}
      <div className="gdoc-header">
        <div className="gdoc-header-republic">Repubulika y'u Rwanda</div>
        <div className="gdoc-header-title">Inyandiko y'ikirego</div>
        <div className="gdoc-header-docnum">{docNumber}</div>
        <div className="gdoc-header-motto">"Ubumwe, Umurimo, Gukunda Igihugu"</div>
      </div>

      {/* Top Row: Date + Village */}
      <div className="gdoc-top-row">
        <div className="gdoc-village-info">
          <h4><i className="bi bi-geo-alt-fill" /> Amakuru y'Umudugudu</h4>
          <div className="gdoc-village-grid">
            <div className="gdoc-village-item"><strong>Intara:</strong> {userVillage.intara || "---"}</div>
            <div className="gdoc-village-item"><strong>Akarere:</strong> {userVillage.akarere || "---"}</div>
            <div className="gdoc-village-item"><strong>Umurenge:</strong> {userVillage.umurenge || "---"}</div>
            <div className="gdoc-village-item"><strong>Akagari:</strong> {userVillage.akagari || "---"}</div>
            <div className="gdoc-village-item"><strong>Umudugudu:</strong> {userVillage.umudugudu || "---"}</div>
          </div>
        </div>
        <div className="gdoc-date-box">
          <div className="gdoc-date-label">Itariki</div>
          <div className="gdoc-date-value">{formatDate()}</div>
        </div>
      </div>

      {/* Complainant Section */}
      <div className="gdoc-section">
        <div className="gdoc-section-title">
          <i className="bi bi-person-fill" /> Amakuru y'uwatanze ikirego
        </div>
        <div className="gdoc-section-body">
          <div className={`gdoc-row ${isPreview ? "gdoc-row-full" : ""}`}>
            <div className="gdoc-field">
              <label>Amazina Yuzuye <span className="required">*</span></label>
              {isPreview ? (
                <div style={{ padding: "10px 0", fontSize: "14px", fontWeight: 500 }}>{form.complainant_name || "---"}</div>
              ) : (
                <input type="text" name="complainant_name" value={form.complainant_name} onChange={handleChange} placeholder="Andika amazina y'uwatanze ikirego" required />
              )}
            </div>
            <div className="gdoc-field">
              <label>Numero y'Irangamuntu</label>
              {isPreview ? (
                <div style={{ padding: "10px 0", fontSize: "14px", fontWeight: 500 }}>{form.national_id || "---"}</div>
              ) : (
                <input type="text" name="national_id" value={form.national_id} onChange={handleChange} placeholder="Indangamuntu 16" maxLength={16} />
              )}
              {!isPreview && <div className="gdoc-char-count">{form.national_id.length}/16</div>}
            </div>
            <div className="gdoc-field">
              <label>Telephone</label>
              {isPreview ? (
                <div style={{ padding: "10px 0", fontSize: "14px", fontWeight: 500 }}>{form.phone || "---"}</div>
              ) : (
                <input type="text" name="phone" value={form.phone} onChange={handleChange} placeholder="078xxxxxxx" />
              )}
            </div>
            <div className="gdoc-field">
              <label>Aho ikibazo cyabereye</label>
              {isPreview ? (
                <div style={{ padding: "10px 0", fontSize: "14px", fontWeight: 500 }}>{form.location || "---"}</div>
              ) : (
                <input type="text" name="location" value={form.location} onChange={handleChange} placeholder="Andika aho byabereye" />
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Complaint Details */}
      <div className="gdoc-section">
        <div className="gdoc-section-title">
          <i className="bi bi-file-text-fill" /> Ibisobanuro by'ikirego
        </div>
        <div className="gdoc-section-body">
          <div className="gdoc-field">
            {isPreview ? (
              <div style={{
                minHeight: "250px",
                padding: "14px 16px",
                background: "#fafbfc",
                border: "1px solid #e2e8f0",
                borderRadius: "5px",
                fontSize: "14px",
                lineHeight: "1.8",
                whiteSpace: "pre-wrap",
                color: form.description ? "#1e293b" : "#94a3b8"
              }}>
                {form.description || "--- Nta bisobanuro byatanzwe ---"}
              </div>
            ) : (
              <textarea
                name="description"
                value={form.description}
                onChange={handleChange}
                placeholder="Sobanura mu buryo burambuye ikirego cyawe..."
                className="gdoc-description-area"
                required
              />
            )}
          </div>
        </div>
      </div>

      {/* Izina ry'Isibo */}
      <div className="gdoc-section">
        <div className="gdoc-section-title">
          <i className="bi bi-building" /> Isibo
        </div>
        <div className="gdoc-section-body">
          <div className="gdoc-row">
            <div className="gdoc-field">
              <label>Izina ry'Isibo <span className="required">*</span></label>
              {isPreview ? (
                <div style={{ padding: "10px 0", fontSize: "14px", fontWeight: 500 }}>{form.izina_isibo || "---"}</div>
              ) : (
                <input type="text" name="izina_isibo" value={form.izina_isibo} onChange={handleChange} placeholder="Andika izina ry'isibo" required />
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Witness Section */}
      <div className="gdoc-section">
        <div className="gdoc-section-title">
          <i className="bi bi-people-fill" /> Abatangabuhamya ({witnesses.length})
        </div>
        <div className="gdoc-section-body">
          {witnesses.length > 0 && (
            <div className="gdoc-witness-list">
              {witnesses.map((w, i) => (
                <div key={i} className="gdoc-witness-item">
                  <i className="bi bi-check-circle-fill" />
                  <span className="gdoc-witness-name">{w.witness_name}</span>
                  {w.signature_image && (
                    <img src={w.signature_image} alt="sig" style={{ maxHeight: 28, borderRadius: 3 }} />
                  )}
                  {!isPreview && (
                    <button className="gdoc-witness-remove" onClick={() => removeWitness(i, w)} type="button" title="Siba">
                      <i className="bi bi-x-lg" />
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}

          {witnesses.length === 0 && !isPreview && (
            <div className="gdoc-alert gdoc-alert-warning">
              <i className="bi bi-exclamation-triangle" /> Nta mutangabuhamya wongewemo. Ugomba kugira nibura abatangabuhamya 4.
            </div>
          )}

          {witnesses.length > 0 && witnesses.length < 4 && !isPreview && (
            <div className="gdoc-alert gdoc-alert-warning">
              <i className="bi bi-exclamation-triangle" /> Ugomba kugira nibura abatangabuhamya 4 (ubu ufite: {witnesses.length})
            </div>
          )}

          {witnesses.length >= 4 && witnesses.length < 5 && !isPreview && (
            <div className="gdoc-alert gdoc-alert-success">
              <i className="bi bi-check-circle" /> Ufite abatangabuhamya bahagije ({witnesses.length}/4). Ushobora kwongera umwe (max 5).
            </div>
          )}

          {!canAddWitness && !isPreview && (
            <div className="gdoc-alert gdoc-alert-warning">
              <i className="bi bi-exclamation-triangle" /> Ntushobora kongeraho abatangabuhamya barenze 5
            </div>
          )}

          {/* Witness Add Form */}
          {canAddWitness && !isPreview && step === "witnesses" && (
            <div className="gdoc-witness-add-card">
              <h5><i className="bi bi-person-plus-fill" /> Ongeraho Umutangabuhamya {witnesses.length + 1}</h5>
              <div className="gdoc-row">
                <div className="gdoc-field">
                  <label>Amazina <span className="required">*</span></label>
                  <input type="text" value={witnessName} onChange={(e) => setWitnessName(e.target.value)} placeholder="Andika amazina y'umuhamya" />
                </div>
              </div>
              <SignaturePad onSave={(data) => setWitnessSig(data)} />
              <div style={{ marginTop: 10 }}>
                <button className="gdoc-btn gdoc-btn-primary gdoc-btn-sm" onClick={saveWitness} disabled={witnessSaving || !witnessName.trim() || !witnessSig}>
                  {witnessSaving ? (
                    <span className="gdoc-loading"><span className="gdoc-spinner" /> Bitegereza...</span>
                  ) : (
                    <><i className="bi bi-save" /> Kubika Umuhamya</>
                  )}
                </button>
              </div>
            </div>
          )}

          {isPreview && witnesses.length === 0 && (
            <div style={{ padding: "14px 0", fontSize: "13px", color: "#94a3b8", fontStyle: "italic" }}>
              Nta batangabuhamya
            </div>
          )}
        </div>
      </div>

      {/* Signature Section — only show in preview/after form submission */}
      {isPreview && (
      <div className="gdoc-section">
        <div className="gdoc-section-title">
          <i className="bi bi-pencil-square" /> Abasinye
        </div>
        <div className="gdoc-section-body">
          <div className="gdoc-signatures">
            {/* Person 1: Complainant */}
            <div className="gdoc-signature-slot">
              <div className="gdoc-sig-label">1. Uwatanze Ikirego</div>
              <div className="gdoc-sig-name">{form.complainant_name || "---"}</div>
              {complainantSig ? (
                <img src={complainantSig} alt="Umukono" className="gdoc-sig-image" />
              ) : (
                <div className="gdoc-sig-placeholder">---</div>
              )}
              <div>
                {complainantSigConfirmed ? (
                  <span className="gdoc-sig-status signed"><i className="bi bi-check-circle-fill" /> Yasinye</span>
                ) : (
                  <span className="gdoc-sig-status pending"><i className="bi bi-clock" /> Ntasinye</span>
                )}
              </div>
            </div>

            {/* Person 2-5: Witnesses */}
            {[0, 1, 2, 3].map((i) => (
              <div key={`w-sig-${i}`} className="gdoc-signature-slot">
                <div className="gdoc-sig-label">{i + 2}. Umutangabuhamya {i + 1}</div>
                <div className="gdoc-sig-name">{witnesses[i]?.witness_name || "---"}</div>
                {witnesses[i]?.signature_image ? (
                  <img src={witnesses[i].signature_image} alt="Umukono" className="gdoc-sig-image" />
                ) : (
                  <div className="gdoc-sig-placeholder">---</div>
                )}
                <div>
                  {witnesses[i] ? (
                    <span className="gdoc-sig-status signed"><i className="bi bi-check-circle-fill" /> Yasinye</span>
                  ) : (
                    <span className="gdoc-sig-status pending"><i className="bi bi-clock" /> Ntasinye</span>
                  )}
                </div>
              </div>
            ))}

            {/* Person 6: Umukuru w'Umudugudu */}
            <div className="gdoc-signature-slot" style={{ borderColor: "#d4a843", background: "#fffcf0" }}>
              <div className="gdoc-sig-label" style={{ color: "#b8860b" }}>6. Umukuru w'Umudugudu</div>
              <div className="gdoc-sig-name">{userName || "---"}</div>
              {muduguduSigned ? (
                <>
                  <img src={muduguduSigned} alt="Umukono" className="gdoc-sig-image" />
                  <span className="gdoc-sig-status signed"><i className="bi bi-check-circle-fill" /> Yasinye</span>
                </>
              ) : (
                <div className="gdoc-sig-placeholder">---</div>
              )}
              {!muduguduSigned && (
                <span className="gdoc-sig-status pending"><i className="bi bi-clock" /> Ntasinye</span>
              )}
            </div>
          </div>
        </div>
      </div>
      )}
    </>
  );

  /* ── Signature Step ── */
  const renderSignatureStep = () => (
    <div className="gdoc-section">
      <div className="gdoc-section-title">
        <i className="bi bi-pencil-square" /> Umukono w'uwatanze Ikirego
      </div>
      <div className="gdoc-section-body">
        <div className="gdoc-alert gdoc-alert-info">
          <i className="bi bi-info-circle" /> Nyamukono hano hejuru hanyuma ukande "Emeza Umukono"
        </div>
        <div className="gdoc-bordered-box">
          <div style={{ fontWeight: 600, marginBottom: 8, fontSize: 14 }}>
            <i className="bi bi-person-fill" /> Umukono wa: {complainantName}
          </div>
          <SignaturePad onSave={handleComplainantSign} />
        </div>
        <div style={{ marginTop: 14, textAlign: "center" }}>
          <button className="gdoc-btn gdoc-btn-success gdoc-btn-lg" onClick={saveComplainantSignature} disabled={!complainantSig}>
            <i className="bi bi-arrow-right" /> Komeza
          </button>
        </div>
      </div>
    </div>
  );

  /* ── Ready/Success Step ── */
  const renderReadyStep = () => (
    <div className="gdoc-success">
      <i className="bi bi-check-circle-fill" />
      <h3>Icyirego cyoherejwe ku Mudugudu!</h3>
      <p>Icyirego cyanyu cyoherejwe neza. Muzakira amakuru y'ibikurikira.</p>
      <button className="gdoc-btn gdoc-btn-primary gdoc-btn-lg" onClick={() => {
        setStep("form");
        setComplaintId(null);
        setComplainantSig(null);
        setComplainantSigConfirmed(false);
        setWitnesses([]);
        setWitnessName("");
        setWitnessSig(null);
        setMuduguduSigned(false);
      }}>
        <i className="bi bi-plus-lg" /> Kwandika Ikindi Cyirego
      </button>
    </div>
  );

  /* ── Main Render ── */
  return (
    <>
      <Styles />
      <div className="gdoc-body">
        <div className="gdoc-paper" ref={paperRef}>

          {/* Step Indicator */}
          {step !== "form" && (
            <div className="gdoc-steps">
              <span className={`gdoc-step ${step === "sign" || step === "witnesses" || step === "isibo_name" || step === "ready" ? "completed" : ""}`}>
                <i className="bi bi-check-circle-fill" /> Kwandika
              </span>
              <div className={`gdoc-step-line ${step === "sign" || step === "witnesses" || step === "isibo_name" || step === "ready" ? "completed" : ""}`} />
              <span className={`gdoc-step ${step === "sign" ? "active" : ""} ${step === "witnesses" || step === "isibo_name" || step === "ready" ? "completed" : "pending"}`}>
                <i className={`bi ${step === "sign" ? "bi-pencil-fill" : step === "witnesses" || step === "isibo_name" || step === "ready" ? "bi-check-circle-fill" : "bi-circle"}`} /> Umukono
              </span>
              <div className={`gdoc-step-line ${step === "witnesses" || step === "isibo_name" || step === "ready" ? "completed" : ""}`} />
              <span className={`gdoc-step ${step === "witnesses" ? "active" : ""} ${step === "isibo_name" || step === "ready" ? "completed" : "pending"}`}>
                <i className={`bi ${step === "witnesses" ? "bi-people-fill" : step === "isibo_name" || step === "ready" ? "bi-check-circle-fill" : "bi-circle"}`} /> Abatangabuhamya
              </span>
              <div className={`gdoc-step-line ${step === "isibo_name" || step === "ready" ? "completed" : ""}`} />
              <span className={`gdoc-step ${step === "isibo_name" ? "active" : ""} ${step === "ready" ? "completed" : "pending"}`}>
                <i className={`bi ${step === "isibo_name" ? "bi-send-fill" : step === "ready" ? "bi-check-circle-fill" : "bi-circle"}`} /> Ohereza
              </span>
            </div>
          )}

          {/* Step: Form */}
          {step === "form" && (
            <form onSubmit={handleSubmit}>
              {renderDocumentContent(false)}
              <div className="gdoc-actions">
                <button type="button" className="gdoc-btn gdoc-btn-outline" onClick={handlePreview}>
                  <i className="bi bi-eye" /> Reba Preview
                </button>
                <button type="submit" className="gdoc-btn gdoc-btn-primary gdoc-btn-lg" disabled={submitting} style={{ flex: 1 }}>
                  {submitting ? (
                    <span className="gdoc-loading"><span className="gdoc-spinner" /> Bitegereza...</span>
                  ) : (
                    <><i className="bi bi-save" /> Bika Draft</>
                  )}
                </button>
              </div>
            </form>
          )}

          {/* Step: Signature */}
          {step === "sign" && (
            <>
              {renderDocumentContent(true)}
              <div style={{ margin: "20px 0 10px", borderTop: "2px solid #e2e8f0" }} />
              {renderSignatureStep()}
            </>
          )}

          {/* Step: Witnesses */}
          {step === "witnesses" && (
            <>
              {renderDocumentContent(true)}
              <div style={{ margin: "20px 0 10px", borderTop: "2px solid #e2e8f0" }} />
              <div className="gdoc-section">
                <div className="gdoc-section-title">
                  <i className="bi bi-people-fill" /> Ongeraho Abatangabuhamya
                </div>
                <div className="gdoc-section-body">
                  <div className="gdoc-alert gdoc-alert-info">
                    <i className="bi bi-info-circle" /> Ugomba kugira nibura abatangabuhamya 4 kandi utarenze 5
                  </div>

                  {witnesses.length > 0 && (
                    <div className="gdoc-witness-list">
                      {witnesses.map((w, i) => (
                        <div key={i} className="gdoc-witness-item">
                          <i className="bi bi-check-circle-fill" />
                          <span className="gdoc-witness-name">Umuhamya {i + 1}: {w.witness_name}</span>
                          {w.signature_image && <img src={w.signature_image} alt="sig" style={{ maxHeight: 28, borderRadius: 3 }} />}
                          <button className="gdoc-witness-remove" onClick={() => removeWitness(i, w)} type="button">
                            <i className="bi bi-x-lg" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}

                  {canAddWitness && (
                    <div className="gdoc-witness-add-card">
                      <h5><i className="bi bi-person-plus-fill" /> Ongeraho Umutangabuhamya {witnesses.length + 1}</h5>
                      <div className="gdoc-row">
                        <div className="gdoc-field">
                          <label>Amazina <span className="required">*</span></label>
                          <input type="text" value={witnessName} onChange={(e) => setWitnessName(e.target.value)} placeholder="Andika amazina y'umuhamya" />
                        </div>
                      </div>
                      <SignaturePad onSave={(data) => setWitnessSig(data)} />
                      <div style={{ marginTop: 10 }}>
                        <button className="gdoc-btn gdoc-btn-primary gdoc-btn-sm" onClick={saveWitness} disabled={witnessSaving || !witnessName.trim() || !witnessSig}>
                          {witnessSaving ? (
                            <span className="gdoc-loading"><span className="gdoc-spinner" /> Bitegereza...</span>
                          ) : (
                            <><i className="bi bi-save" /> Kubika Umuhamya</>
                          )}
                        </button>
                      </div>
                    </div>
                  )}

                  {!canAddWitness && (
                    <div className="gdoc-alert gdoc-alert-warning">
                      <i className="bi bi-exclamation-triangle" /> Ntushobora kongeraho abatangabuhamya barenze 5
                    </div>
                  )}

                  {witnesses.length < 4 && (
                    <div className="gdoc-alert gdoc-alert-warning">
                      <i className="bi bi-exclamation-triangle" /> Ugomba kugira nibura 4 abatangabuhamya (ubu ufite: {witnesses.length})
                    </div>
                  )}
                </div>
              </div>
              <div className="gdoc-actions">
                {(witnesses.length >= 4) && (
                  <button className="gdoc-btn gdoc-btn-success gdoc-btn-lg gdoc-btn-block" onClick={() => setStep("isibo_name")}>
                    <i className="bi bi-arrow-right" /> Komeza
                  </button>
                )}
              </div>
            </>
          )}

          {/* Step: Send */}
          {step === "isibo_name" && (
            <>
              {renderDocumentContent(true)}
              <div style={{ margin: "20px 0 10px", borderTop: "2px solid #e2e8f0" }} />
              <div className="gdoc-section">
                <div className="gdoc-section-title">
                  <i className="bi bi-send-fill" /> Ohereza Icyirego
                </div>
                <div className="gdoc-section-body">
                  <div className="gdoc-send-card">
                    <h4><i className="bi bi-check-circle-fill" /> Icyirego cyuzuye</h4>
                    <p>Reba ibisobanuro by'ikirego hanyuma wohereze ku Mudugudu</p>
                    <div className="gdoc-send-summary">
                      <div className="gdoc-send-summary-item">
                        <i className="bi bi-person-fill" /> <strong>{complainantName}</strong>
                      </div>
                      <div className="gdoc-send-summary-item">
                        <i className="bi bi-people-fill" /> <strong>{witnesses.length}</strong> abatangabuhamya
                      </div>
                      <div className="gdoc-send-summary-item">
                        <i className="bi bi-file-text-fill" /> <strong>{docNumber}</strong>
                      </div>
                    </div>
                    <button className="gdoc-btn gdoc-btn-success gdoc-btn-lg gdoc-btn-block" onClick={handleSendToUmudugudu} disabled={sending}>
                      {sending ? (
                        <span className="gdoc-loading"><span className="gdoc-spinner" /> Bitegereza...</span>
                      ) : (
                        <><i className="bi bi-send-fill" /> Ohereza ku Mudugudu</>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            </>
          )}

          {/* Step: Ready */}
          {step === "ready" && renderReadyStep()}

          {/* ── Action Buttons (non-form steps) ── */}
          {step !== "form" && step !== "ready" && (
            <div className="gdoc-actions">
              <button type="button" className="gdoc-btn gdoc-btn-outline" onClick={handlePreview}>
                <i className="bi bi-eye" /> Reba Preview
              </button>
              <button type="button" className="gdoc-btn gdoc-btn-outline" onClick={handlePrint}>
                <i className="bi bi-printer" /> Print
              </button>
            </div>
          )}

        </div>

      </div>

      {/* ── Preview Modal ── */}
      {showPreview && (
        <div className="gdoc-preview-overlay" onClick={() => setShowPreview(false)}>
          <div className="gdoc-preview-modal" onClick={(e) => e.stopPropagation()}>
            <div className="gdoc-preview-header">
              <h3><i className="bi bi-file-earmark-text" /> Preview - Inyandiko Y'ikirego</h3>
              <div>
                <button className="gdoc-btn gdoc-btn-sm gdoc-btn-outline" onClick={handlePrint} style={{ marginRight: 8 }}>
                  <i className="bi bi-printer" /> Print
                </button>
                <button className="gdoc-preview-close" onClick={() => setShowPreview(false)}>&times;</button>
              </div>
            </div>
            {renderDocumentContent(true)}
          </div>
        </div>
      )}
    </>
  );
}
